import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// Components
import Sidebar from './components/Sidebar';
import Topbar from './components/Topbar';
import Footer from './components/Footer';
import Login from './components/Login';
import ProtectedRoute, { DashboardRoute, AutomationRunsRoute, SuperAdminRoute, ManagerOrAboveRoute } from './components/ProtectedRoute';
import Dashboard from './pages/Dashboard';
import ClosingOfTheDay from './pages/ClosingOfTheDay';
import ScheduledAppointments from './pages/ScheduledAppointments';
import AutomationRuns from './pages/AutomationRuns';
import AutomationLogs from './pages/AutomationLogs';
import Commitments from './pages/Commitments';
import FileCommissions from './pages/FileCommissions';

// Filtered Automation Pages
import BothAgentsPresent from './pages/BothAgentsPresent';
import ListingAgentsOnly from './pages/ListingAgentsOnly';
import SellingAgentsOnly from './pages/SellingAgentsOnly';
import FSBONoAgents from './pages/FSBONoAgents';
import AgentCopyMissing from './pages/AgentCopyMissing';
import BothAgentsMissing from './pages/BothAgentsMissing';
import NoCompletionStatus from './pages/NoCompletionStatus';
import FailedAutomation from './pages/FailedAutomation';

// Commission Pages
import SalesPersons from './pages/SalesPersons';
import SalesPersonCommissions from './pages/SalesPersonCommissions';
import ManageSalesPersons from './pages/ManageSalesPersons';
import ManageTransactionCoordinators from './pages/ManageTransactionCoordinators';
import ManageProcessors from './pages/ManageProcessors';
import ManageManagers from './pages/ManageManagers';

// Permission Example Component

// Services
import { apiService } from './services/apiService';

// Layout component for authenticated pages
const AuthenticatedLayout = ({ children }) => (
  <div id="wrapper">
    {/* Sidebar */}
    <Sidebar />
    
    {/* Content Wrapper */}
    <div id="content-wrapper" className="d-flex flex-column">
      {/* Main Content */}
      <div id="content">
        {/* Topbar */}
        <Topbar />
        
        {/* Begin Page Content */}
        <div className="container-fluid">
          {children}
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  </div>
);

function App() {
  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        
        {/* Protected Routes */}
        <Route path="/" element={
          <ProtectedRoute>
            <Navigate to="/dashboard" replace />
          </ProtectedRoute>
        } />
        
        <Route path="/dashboard" element={
          <DashboardRoute>
            <AuthenticatedLayout>
              <Dashboard />
            </AuthenticatedLayout>
          </DashboardRoute>
        } />
        
        <Route path="/closing-of-the-day" element={
          <DashboardRoute>
            <AuthenticatedLayout>
              <ClosingOfTheDay />
            </AuthenticatedLayout>
          </DashboardRoute>
        } />
        
        <Route path="/scheduled-appointments" element={
          <DashboardRoute>
            <AuthenticatedLayout>
              <ScheduledAppointments />
            </AuthenticatedLayout>
          </DashboardRoute>
        } />
        
        <Route path="/automation-runs" element={
          <AutomationRunsRoute>
            <AuthenticatedLayout>
              <AutomationRuns />
            </AuthenticatedLayout>
          </AutomationRunsRoute>
        } />
        
        <Route path="/scheduled-appointments" element={
          <DashboardRoute>
            <AuthenticatedLayout>
              <ScheduledAppointments />
            </AuthenticatedLayout>
          </DashboardRoute>
        } />
        
        <Route path="/commitments" element={
          <DashboardRoute>
            <AuthenticatedLayout>
              <Commitments />
            </AuthenticatedLayout>
          </DashboardRoute>
        } />
        
        <Route path="/automation/logs/:automationRunId" element={
          <AutomationRunsRoute>
            <AuthenticatedLayout>
              <AutomationLogs />
            </AuthenticatedLayout>
          </AutomationRunsRoute>
        } />
        
        {/* Filtered Automation Routes */}
        <Route path="/automation/both-agents-present" element={
          <AutomationRunsRoute>
            <AuthenticatedLayout>
              <BothAgentsPresent />
            </AuthenticatedLayout>
          </AutomationRunsRoute>
        } />
        
        <Route path="/automation/listing-agents-only" element={
          <AutomationRunsRoute>
            <AuthenticatedLayout>
              <ListingAgentsOnly />
            </AuthenticatedLayout>
          </AutomationRunsRoute>
        } />
        
        <Route path="/automation/selling-agents-only" element={
          <AutomationRunsRoute>
            <AuthenticatedLayout>
              <SellingAgentsOnly />
            </AuthenticatedLayout>
          </AutomationRunsRoute>
        } />
        
        <Route path="/automation/fsbo-no-agents" element={
          <AutomationRunsRoute>
            <AuthenticatedLayout>
              <FSBONoAgents />
            </AuthenticatedLayout>
          </AutomationRunsRoute>
        } />
        
        <Route path="/automation/agent-copy-missing" element={
          <AutomationRunsRoute>
            <AuthenticatedLayout>
              <AgentCopyMissing />
            </AuthenticatedLayout>
          </AutomationRunsRoute>
        } />
        
        <Route path="/automation/both-agents-missing" element={
          <AutomationRunsRoute>
            <AuthenticatedLayout>
              <BothAgentsMissing />
            </AuthenticatedLayout>
          </AutomationRunsRoute>
        } />
        
        <Route path="/automation/no-completion-status" element={
          <AutomationRunsRoute>
            <AuthenticatedLayout>
              <NoCompletionStatus />
            </AuthenticatedLayout>
          </AutomationRunsRoute>
        } />
        
        <Route path="/automation/failed" element={
          <AutomationRunsRoute>
            <AuthenticatedLayout>
              <FailedAutomation />
            </AuthenticatedLayout>
          </AutomationRunsRoute>
        } />
        
        {/* Commission Routes */}
        <Route path="/salespersons" element={
          <DashboardRoute>
            <AuthenticatedLayout>
              <SalesPersons />
            </AuthenticatedLayout>
          </DashboardRoute>
        } />
        
        <Route path="/salespersons/:salespersonId/commissions" element={
          <DashboardRoute>
            <AuthenticatedLayout>
              <SalesPersonCommissions />
            </AuthenticatedLayout>
          </DashboardRoute>
        } />
        
        <Route path="/commissions" element={
          <DashboardRoute>
            <AuthenticatedLayout>
              <FileCommissions />
            </AuthenticatedLayout>
          </DashboardRoute>
        } />
        
        {/* New Management Routes */}
        <Route path="/manage/salespersons" element={
          <ManagerOrAboveRoute>
            <AuthenticatedLayout>
              <ManageSalesPersons />
            </AuthenticatedLayout>
          </ManagerOrAboveRoute>
        } />
        <Route path="/manage/transaction-coordinators" element={
          <ManagerOrAboveRoute>
            <AuthenticatedLayout>
              <ManageTransactionCoordinators />
            </AuthenticatedLayout>
          </ManagerOrAboveRoute>
        } />
        <Route path="/manage/processors" element={
          <ManagerOrAboveRoute>
            <AuthenticatedLayout>
              <ManageProcessors />
            </AuthenticatedLayout>
          </ManagerOrAboveRoute>
        } />
        <Route path="/manage/managers" element={
          <ManagerOrAboveRoute>
            <AuthenticatedLayout>
              <ManageManagers />
            </AuthenticatedLayout>
          </ManagerOrAboveRoute>
        } />
        
        {/* Management Routes - Only for super_admin and manager */}
        <Route path="/users" element={
          <ManagerOrAboveRoute>
            <AuthenticatedLayout>
              <div>Users Management Page (To be implemented)</div>
            </AuthenticatedLayout>
          </ManagerOrAboveRoute>
        } />
        
        <Route path="/roles" element={
          <SuperAdminRoute>
            <AuthenticatedLayout>
              <div>Roles Management Page (To be implemented)</div>
            </AuthenticatedLayout>
          </SuperAdminRoute>
        } />
        
        {/* System Routes - Only for super_admin */}
        <Route path="/settings" element={
          <SuperAdminRoute>
            <AuthenticatedLayout>
              <div>Settings Page (To be implemented)</div>
            </AuthenticatedLayout>
          </SuperAdminRoute>
        } />
        
        <Route path="/logs" element={
          <SuperAdminRoute>
            <AuthenticatedLayout>
              <div>System Logs Page (To be implemented)</div>
            </AuthenticatedLayout>
          </SuperAdminRoute>
        } />
        
        
        
        {/* Catch all route - redirect to dashboard if authenticated, login if not */}
        <Route path="*" element={
          apiService.isAuthenticated() ? 
            <Navigate to="/dashboard" replace /> : 
            <Navigate to="/login" replace />
        } />
      </Routes>
    </Router>
  );
}

export default App;
